import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, MinusCircle, AlertTriangle, Eye, X } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Modal from '../components/Modal';
import { FF, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import SearchableSelect from '../components/SearchableSelect';

export default function Toner() {
  const { isIT } = useAuth();
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterLow, setFilterLow] = useState(false);
  const [filterIssueMin, setFilterIssueMin] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [printers, setPrinters] = useState([]);
  const [tonerModels, setTonerModels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState('');
  // Multi-printer selection state
  const [selectedPrinters, setSelectedPrinters] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      const r = await api.get('/toner', { params });
      let data = r.data.data || [];
      if (search) data = data.filter(t =>
        t.toner_model?.toLowerCase().includes(search.toLowerCase()) ||
        t.compatible_printer?.toLowerCase().includes(search.toLowerCase()) ||
        t.vendor?.toLowerCase().includes(search.toLowerCase())
      );
      if (filterLow) data = data.filter(t => t.quantity_in_stock <= t.reorder_level);
      if (filterIssueMin !== '') data = data.filter(t => (t.total_issued || 0) >= parseInt(filterIssueMin));
      if (filterCompany) data = data.filter(t => t.company_id === filterCompany);
      setRows(data); setTotal(data.length);
    } catch {} finally { setLoading(false); }
  }, [search, filterLow, filterIssueMin, dateFrom, dateTo, filterCompany]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/master/printers').then(r => setPrinters(r.data || [])).catch(() => {});
    api.get('/master/toner-models').then(r => setTonerModels(r.data || [])).catch(() => {});
    api.get('/employees/meta/companies').then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  // When opening edit, parse stored printer string back into selectedPrinters array
  const openEdit = (r) => {
    setSelected(r);
    const stored = r.compatible_printer || '';
    const parsed = stored ? stored.split('|').map(s => s.trim()).filter(Boolean) : [];
    setSelectedPrinters(parsed);
    setForm({ ...r });
    setError(''); setModal('form');
  };

  const openAdd = () => {
    setSelected(null);
    setSelectedPrinters([]);
    setForm({ quantity_in_stock: 0, reorder_level: 2 });
    setError(''); setModal('form');
  };

  const addPrinter = (val) => {
    if (!val || selectedPrinters.includes(val)) return;
    setSelectedPrinters(prev => [...prev, val]);
  };

  const removePrinter = (val) => {
    setSelectedPrinters(prev => prev.filter(p => p !== val));
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (modal === 'issue') {
        await api.post(`/toner/${selected.id}/issue`, {
          quantity: form.quantity, remarks: form.remarks,
          issued_date: form.issued_date || new Date().toISOString().split('T')[0],
          printer_asset_id: form.printer_asset_id
        });
      } else {
        // Join selected printers with pipe separator
        const payload = { ...form, compatible_printer: selectedPrinters.join(' | ') };
        if (!selected) await api.post('/toner', payload);
        else await api.put(`/toner/${selected.id}`, payload);
      }
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const lowStock = rows.filter(r => r.quantity_in_stock <= r.reorder_level).length;

  const columns = [
    { header: 'Toner Model', render: r => (
      <div className="flex items-center gap-2">
        <span className="font-medium" style={{color:'#e2e8f0'}}>{r.toner_model}</span>
        {r.quantity_in_stock <= r.reorder_level && <AlertTriangle size={13} style={{color:'#f59e0b'}}/>}
      </div>
    )},
    { header: 'Compatible Printers', render: r => {
      const printerList = (r.compatible_printer || '').split('|').map(p => p.trim()).filter(Boolean);
      if (!printerList.length) return <span style={{color:'#334155'}}>—</span>;
      return (
        <div className="space-y-0.5">
          {printerList.map((p, i) => <div key={i} className="text-xs" style={{color:'#94a3b8'}}>{p}</div>)}
        </div>
      );
    }},
    { header: 'In Stock', render: r => (
      <span className="font-bold text-lg" style={{color:r.quantity_in_stock <= r.reorder_level?'#f87171':'#34d399'}}>
        {r.quantity_in_stock}
      </span>
    )},
    { header: 'Issued (Total)', render: r => (
      <span className="font-semibold" style={{color:'#f97316'}}>{r.total_issued ?? 0}</span>
    )},
    { header: 'Reorder At', render: r => <span style={{color:'#475569'}}>{r.reorder_level}</span> },
    { header: 'Unit Price', render: r => r.unit_price ? `LKR ${Number(r.unit_price).toLocaleString()}` : <span style={{color:'#334155'}}>—</span> },
    { header: 'Company', render: r => r.company_name || <span style={{color:'#334155'}}>—</span> },
    { header: 'Vendor', render: r => r.vendor || <span style={{color:'#334155'}}>—</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        <button onClick={() => { setSelected(r); setModal('view'); }} className="btn-ghost px-2 py-1 text-xs" title="View" style={{color:'#60a5fa'}}><Eye size={13}/></button>
        {isIT && <button onClick={() => { setSelected(r); setForm({quantity:1,issued_date:new Date().toISOString().split('T')[0]}); setError(''); setModal('issue'); }} className="btn-ghost px-2 py-1 text-xs" style={{color:'#f59e0b'}} title="Issue"><MinusCircle size={13}/></button>}
        {isIT && <button onClick={() => openEdit(r)} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={13}/></button>}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Toner Management" count={total}>
        <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
        {isIT && <button onClick={openAdd} className="btn-primary"><Plus size={13}/> Add Toner</button>}
      </PageHeader>

      {lowStock > 0 && (
        <div className="glass rounded-xl p-3 flex items-center gap-3" style={{border:'1px solid rgba(245,158,11,0.3)',background:'rgba(245,158,11,0.06)'}}>
          <AlertTriangle size={15} style={{color:'#f59e0b',flexShrink:0}}/>
          <span className="text-sm" style={{color:'#fbbf24'}}><strong>{lowStock}</strong> toner type(s) at or below reorder level — order soon</span>
        </div>
      )}

      <FilterBar hasFilters={!!(search||filterLow||filterIssueMin||dateFrom||dateTo||filterCompany)}
        onClear={() => { setSearch(''); setFilterLow(false); setFilterIssueMin(''); setDateFrom(''); setDateTo(''); setFilterCompany(''); }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search toner model, printer, vendor..."/>
        <FilterSelect value={filterCompany} onChange={setFilterCompany} placeholder="All Companies" options={companies.map(c=>({value:c.id,label:c.name}))}/>
        <button onClick={() => setFilterLow(!filterLow)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors"
          style={{background:filterLow?'rgba(245,158,11,0.15)':'transparent',color:filterLow?'#fbbf24':'#475569',border:`1px solid ${filterLow?'rgba(245,158,11,0.4)':'rgba(255,255,255,0.1)'}`}}>
          <AlertTriangle size={13}/> Low Stock Only
        </button>
        <FilterSelect value={filterIssueMin} onChange={setFilterIssueMin} placeholder="Issued Qty: Any"
          options={[{value:'1',label:'Issued ≥ 1'},{value:'5',label:'Issued ≥ 5'},{value:'10',label:'Issued ≥ 10'},{value:'20',label:'Issued ≥ 20'}]}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); }}/>
      </FilterBar>

      <Table columns={columns} data={rows} loading={loading} page={1} pages={1} total={total} onPage={() => {}}/>

      {/* ── ADD / EDIT MODAL ──────────────────────────────── */}
      {modal === 'form' && (
        <Modal title={selected ? 'Edit Toner Stock' : 'Add Toner Stock'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Toner Model *</label>
                <select value={form.toner_model||''} onChange={e=>setForm({...form,toner_model:e.target.value})} className="input-dark" required>
                  <option value="">Select existing or type below</option>
                  {tonerModels.map(m=><option key={m} value={m}>{m}</option>)}
                </select>
                <input value={form.toner_model||''} onChange={e=>setForm({...form,toner_model:e.target.value})}
                  className="input-dark mt-1" style={{fontSize:11}} placeholder="Or type new toner model..."/>
              </div>

              {/* Multi-printer selection */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Compatible Printers</label>
                <select onChange={e => { addPrinter(e.target.value); e.target.value = ''; }} className="input-dark" defaultValue="">
                  <option value="">Add printer from list...</option>
                  {printers.map(p => (
                    <option key={p.id} value={`${p.brand} ${p.model} (${p.asset_number})`}>
                      {p.brand} {p.model} — {p.asset_number}{p.currentHolder ? ` [${p.currentHolder.full_name}]` : ''}
                    </option>
                  ))}
                </select>
                <input onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPrinter(e.target.value.trim()); e.target.value = ''; } }}
                  className="input-dark mt-1" style={{fontSize:11}} placeholder="Or type printer model and press Enter..."/>
                {/* Selected printers chips */}
                {selectedPrinters.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedPrinters.map((p, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
                        style={{background:'rgba(59,130,246,0.15)',color:'#60a5fa',border:'1px solid rgba(59,130,246,0.3)'}}>
                        {p}
                        <button type="button" onClick={() => removePrinter(p)} style={{color:'#f87171',marginLeft:2}}><X size={9}/></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <FF label="Quantity in Stock" name="quantity_in_stock" type="number" form={form} setForm={setForm}/>
              <FF label="Reorder Level" name="reorder_level" type="number" form={form} setForm={setForm}/>
              <FF label="Unit Price (LKR)" name="unit_price" type="number" form={form} setForm={setForm}/>
              <FF label="Vendor" name="vendor" form={form} setForm={setForm}/>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company</label>
                <SearchableSelect value={form.company_id||''}
                  onChange={v => setForm({...form, company_id: v || null})}
                  options={companies.map(c => ({ value: c.id, label: c.name, hint: c.code }))}
                  placeholder="All / No specific company"/>
              </div>
            </div>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
              <textarea value={form.remarks||''} onChange={e=>setForm({...form,remarks:e.target.value})} rows={2} className="input-dark"/></div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── ISSUE MODAL ───────────────────────────────────── */}
      {modal === 'issue' && (
        <Modal title={`Issue Toner: ${selected?.toner_model}`} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="px-4 py-3 rounded-xl text-sm" style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.25)',color:'#fbbf24'}}>
              Current stock: <strong>{selected?.quantity_in_stock}</strong> units · Total issued so far: <strong>{selected?.total_issued ?? 0}</strong>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Issue to Printer</label>
              <select value={form.printer_asset_id||''} onChange={e=>setForm({...form,printer_asset_id:e.target.value})} className="input-dark">
                <option value="">Select printer (optional)</option>
                {printers.map(p=><option key={p.id} value={p.id}>{p.brand} {p.model} — {p.asset_number}</option>)}
              </select>
            </div>
            <FF label="Quantity *" name="quantity" type="number" form={form} setForm={setForm} required/>
            <FF label="Issue Date" name="issued_date" type="date" form={form} setForm={setForm}/>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Issued To / Department</label>
              <textarea value={form.remarks||''} onChange={e=>setForm({...form,remarks:e.target.value})} rows={2} className="input-dark" placeholder="Who received this toner?"/></div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" style={{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>{saving?'...':'Issue Toner'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── VIEW MODAL ────────────────────────────────────── */}
      {modal === 'view' && selected && (
        <Modal title={`Toner Details — ${selected.toner_model}`} onClose={() => { setModal(null); setSelected(null); }}>
          <div className="space-y-3">
            {[
              ['Toner Model', selected.toner_model],
              ['Vendor', selected.vendor||'—'],
              ['Unit Price', selected.unit_price ? `LKR ${Number(selected.unit_price).toLocaleString()}` : '—'],
              ['In Stock', <span className="font-bold" style={{color:selected.quantity_in_stock<=selected.reorder_level?'#f87171':'#34d399'}}>{selected.quantity_in_stock} units</span>],
              ['Reorder Level', `${selected.reorder_level} units`],
              ['Total Issued', <span style={{color:'#f97316'}}>{selected.total_issued ?? 0} units</span>],
              ['Remarks', selected.remarks||'—'],
            ].map(([k,v]) => (
              <div key={k} className="flex items-start gap-3 py-2" style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                <span className="text-xs flex-shrink-0 w-28" style={{color:'#475569'}}>{k}</span>
                <span className="text-xs flex-1" style={{color:'#e2e8f0'}}>{v}</span>
              </div>
            ))}
            {/* Printer list */}
            <div className="flex items-start gap-3 py-2" style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
              <span className="text-xs flex-shrink-0 w-28" style={{color:'#475569'}}>Compatible Printers</span>
              <div className="flex-1">
                {(selected.compatible_printer||'').split('|').map(p=>p.trim()).filter(Boolean).map((p,i) => (
                  <div key={i} className="text-xs py-0.5" style={{color:'#94a3b8'}}>{p}</div>
                ))}
                {!selected.compatible_printer && <span className="text-xs" style={{color:'#334155'}}>—</span>}
              </div>
            </div>
            {/* Recent usage */}
            {selected.usageHistory?.length > 0 && (
              <div>
                <div className="text-xs font-semibold mb-2" style={{color:'#94a3b8'}}>Recent Issues</div>
                {selected.usageHistory.slice(0,5).map((u,i) => (
                  <div key={i} className="flex items-center justify-between text-xs py-1.5 px-2 rounded-lg mb-1" style={{background:'rgba(255,255,255,0.03)'}}>
                    <span style={{color:'#475569'}}>{u.issued_date||'—'}</span>
                    <span style={{color:'#f97316'}}>{u.quantity} unit{u.quantity!==1?'s':''}</span>
                    <span style={{color:'#334155'}}>{u.remarks||'—'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
